import type { ProviderId, StorageClass } from './priceBook'

/**
 * What the visitor describes: provider-neutral resources, the traffic they
 * serve, and any discount they have negotiated. Nothing here names an instance
 * type — each provider's closest match is worked out from the sizes.
 */

export const OPERATING_SYSTEMS = ['linux', 'windows'] as const
export type OperatingSystem = (typeof OPERATING_SYSTEMS)[number]

export const PERIODS = ['hour', 'month', 'year'] as const
export type Period = (typeof PERIODS)[number]

export const RESOURCE_KINDS = ['vm', 'db', 'disk', 'bucket', 'lb'] as const
export type ResourceKind = (typeof RESOURCE_KINDS)[number]

/** Sizes the pickers offer: the steps every provider sells machines in. */
export const VCPU_OPTIONS = [2, 4, 8, 16, 32, 48, 64] as const
export const MEMORY_OPTIONS = [2, 4, 8, 16, 32, 48, 64, 96, 128, 192, 256, 384, 512] as const

/**
 * `id` keeps a line's identity in the list while lines around it are removed;
 * it is not part of the share link.
 */
interface Line {
  readonly id: number
}

export interface VirtualMachine extends Line {
  readonly kind: 'vm'
  readonly vcpu: number
  readonly memoryGb: number
  readonly os: OperatingSystem
  readonly hoursPerDay: number
  readonly count: number
}

export interface Database extends Line {
  readonly kind: 'db'
  readonly vcpu: number
  readonly memoryGb: number
  readonly storageGb: number
  /** A standby in a second zone that takes over on failure. */
  readonly ha: boolean
  readonly count: number
}

export interface Disk extends Line {
  readonly kind: 'disk'
  readonly sizeGb: number
  readonly count: number
}

export interface Bucket extends Line {
  readonly kind: 'bucket'
  readonly sizeGb: number
  readonly storageClass: StorageClass
}

export interface LoadBalancer extends Line {
  readonly kind: 'lb'
  readonly count: number
}

export type Resource = VirtualMachine | Database | Disk | Bucket | LoadBalancer

/** Between the resources and the internet, for the whole design: free allowances are per account. */
export interface Transfer {
  readonly inboundGb: number
  readonly outboundGb: number
}

export interface Spec {
  readonly resources: readonly Resource[]
  readonly transfer: Transfer
  /** Percent off everything on that provider, 0–100. */
  readonly discounts: Readonly<Record<ProviderId, number>>
  /** How the breakdown is shown; the totals always show all three. */
  readonly period: Period
}

export const LIMITS = {
  resources: 30,
  count: 50,
  minHoursPerDay: 1,
  hoursPerDay: 24,
  /** 10 PB: beyond any design this page is for, low enough to catch a typo. */
  gb: 10_000_000,
  discount: 100,
} as const

type Template<T extends Resource> = Omit<T, 'id'>

/** What a newly added line starts as: a common, mid-sized choice. */
export const NEW_RESOURCE: {
  readonly [K in ResourceKind]: Template<Extract<Resource, { kind: K }>>
} = {
  vm: { kind: 'vm', vcpu: 2, memoryGb: 8, os: 'linux', hoursPerDay: 24, count: 1 },
  db: { kind: 'db', vcpu: 2, memoryGb: 8, storageGb: 100, ha: false, count: 1 },
  disk: { kind: 'disk', sizeGb: 100, count: 1 },
  bucket: { kind: 'bucket', sizeGb: 500, storageClass: 'standard' },
  lb: { kind: 'lb', count: 1 },
}

export const NO_DISCOUNTS: Spec['discounts'] = { aws: 0, gcp: 0, azure: 0, oci: 0 }

/** A small web application: something to compare the moment the page opens. */
export const DEFAULT_SPEC: Spec = {
  resources: [
    { id: 1, kind: 'vm', vcpu: 4, memoryGb: 16, os: 'linux', hoursPerDay: 24, count: 2 },
    { id: 2, kind: 'db', vcpu: 2, memoryGb: 8, storageGb: 100, ha: false, count: 1 },
    { id: 3, kind: 'disk', sizeGb: 100, count: 2 },
    { id: 4, kind: 'bucket', sizeGb: 500, storageClass: 'standard' },
    { id: 5, kind: 'lb', count: 1 },
  ],
  transfer: { inboundGb: 100, outboundGb: 1000 },
  discounts: NO_DISCOUNTS,
  period: 'month',
}

/** Appends a line of `kind`, with an id no other line has. */
export function addResource(resources: readonly Resource[], kind: ResourceKind): Resource[] {
  const id = Math.max(0, ...resources.map((resource) => resource.id)) + 1
  return [...resources, { ...NEW_RESOURCE[kind], id } as Resource]
}

export function isCount(value: number, max: number, min = 0): boolean {
  return Number.isInteger(value) && value >= min && value <= max
}

/** Whole GB only: no provider bills storage or traffic in finer units worth showing. */
export function isValidGb(value: number): boolean {
  return isCount(value, LIMITS.gb)
}

export function isValidDiscount(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= LIMITS.discount
}

/** True when every typed number is usable, so there is something to price. */
export function isValidSpec(spec: Spec): boolean {
  const gbValid = spec.resources.every((resource) => {
    if (resource.kind === 'db') return isValidGb(resource.storageGb)
    if (resource.kind === 'disk' || resource.kind === 'bucket') return isValidGb(resource.sizeGb)
    return true
  })
  return (
    gbValid &&
    isValidGb(spec.transfer.inboundGb) &&
    isValidGb(spec.transfer.outboundGb) &&
    Object.values(spec.discounts).every(isValidDiscount)
  )
}
